import { type CatalogStore } from "../store-contract";
import { type CatalogSnapshot } from "../types";
import { compareStrings, getAllRows, getRow, txDone } from "./db";

// One record per org holding the catalog document as the JSON it was saved
// as — the device store's single-row twin — so the byte size is the stored
// string's own and a load parses exactly what was written.
interface CatalogRow {
  org_id: string;
  etag: string;
  document_json: string;
  byte_size: number;
  saved_at: number;
}

export function createCatalogStore(db: IDBDatabase): CatalogStore {
  const readStore = (): IDBObjectStore =>
    db.transaction("catalogs", "readonly").objectStore("catalogs");

  return {
    async save(orgId, etag, document, now = Date.now()) {
      const documentJson = JSON.stringify(document);
      const tx = db.transaction("catalogs", "readwrite");
      tx.objectStore("catalogs").put({
        org_id: orgId,
        etag,
        document_json: documentJson,
        byte_size: new TextEncoder().encode(documentJson).byteLength,
        saved_at: now,
      } satisfies CatalogRow);
      await txDone(tx);
    },

    async load(orgId) {
      const row = await getRow<CatalogRow>(readStore(), orgId);
      // JSON.parse returns unknown-shaped data; document_json was written
      // from a CatalogSnapshot by save.
      return row === null ? null : (JSON.parse(row.document_json) as CatalogSnapshot);
    },

    async list() {
      const rows = await getAllRows<CatalogRow>(readStore());
      rows.sort((x, y) => compareStrings(x.org_id, y.org_id));
      return rows.map((r) => ({
        orgId: r.org_id,
        etag: r.etag,
        byteSize: r.byte_size,
        savedAt: r.saved_at,
      }));
    },

    async remove(orgId) {
      const tx = db.transaction("catalogs", "readwrite");
      tx.objectStore("catalogs").delete(orgId);
      await txDone(tx);
    },
  };
}
